import { Inject, Injectable, DOCUMENT } from '@angular/core';

import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { Action, Store } from '@ngrx/store';

import { Observable, of, EMPTY } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { UserService } from '../core/user';
import { RefreshService } from '../core/refresh.service';
import * as user from '../actions/user';
import * as settings from '../actions/settings';
import * as fromRoot from '../reducers';
import { AnalyticsService } from '../core/analytics';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../shared/snackbar/snackbar.component';
import { AppConstants } from '../app.constants';
import { WelcomeDialogComponent } from '../discover/welcome-dialog/welcome-dialog.component';
import { DiscoverConstants } from '../discover/discover.constants';
import { LaunchDarklyService } from '../launch-darkly.service';
import { CampaignSource } from '../engage/dependencies/ICampaignSource';
import { DigitalMarketingCampaignSource } from '../engage/dependencies/DigitalMarketingCampaignSource';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class UserEffects {

  public dismissAlert$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.DISMISS_ALERT),
    map((action: any) => action.payload),
    switchMap((alertDate) => this.us.dismissAlert(alertDate.alertId).pipe(
      map((res) => new user.DismissAlertSuccessAction(res))
    ))
  )})

  public dismissBanner$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.DISMISS_BANNER),
    map((action: any) => action.payload),
    switchMap((bannerId) => this.us.updateBannerSettings(bannerId)),
    map((res) => new user.DismissBannerSuccessful(res))
  )})

  public getLocationDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.SELECT_LOCATION),
    map((action: any) => action.payload),
    switchMap((payload) => {
      return this.us.getLocationDetails(payload).pipe(
        map((res) => new user.GetLocationDetailsSuccess(res)),
        catchError((error) => of(new user.GetLocationDetailsFailure(error)))
      );
    })
  )})

  public gethighMessagingAlertDetails$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.SELECT_LOCATION),
    map((action: any) => action.payload),
    concatLatestFrom(() => this.store),
    switchMap(([payload]) => {
      return this.campaignSource.getLocationHighMessagingAlertDetails(payload.locationNumber).pipe(
        map((res) => {
          return new user.GetLocationhighMessagingAlertDetailsSuccess(res);
        })
      );
    })
  )})

  public openLocationWelcomeSnackbar: any = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.GET_LOCATION_DETAILS_SUCCESS),
    switchMap(() => this.store.select(fromRoot.getSelectedLocation).pipe(take(1))),
    concatLatestFrom(() => {
      return [
        this.store.select(fromRoot.getScreenSize)
      ]
    }),
    tap(([location, screenSize]) => {
      const refreshReason = this.refreshService.getRefreshReason();
      // Only launch snackbar on mobile and for actual location changes (not page refresh)
      if (screenSize === AppConstants.extraSmall && refreshReason?.includes('location-change')) {
        let config: MatSnackBarConfig;
        config = {
          horizontalPosition: 'center',
          panelClass: ['location-change', 'fade-out'],
          data: {
            dismiss: false,
            fadeOut: true,
            html: `Welcome to<br><b>${location.locationName}</b>`
          }
        };
        this.snackBar.openFromComponent(SnackbarComponent, config);
      }
      if (refreshReason?.includes('location-change-legacy')) {
        this.refreshService.clearRefreshReason();
      }
    })
  )}, { dispatch : false });

  public openWelcomeModal: any = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.GET_LOCATION_DETAILS_SUCCESS),
    map((action: any) => action.payload),
    filter(loc => loc.notificationBanners.filter((banner => banner.id == DiscoverConstants.welcomeModalBanner))),
    concatLatestFrom(() => {
      return [
        this.store.select(fromRoot.getEulaAgree),
        this.store.select(fromRoot.getFeatureEnabled(AppConstants.maintenanceFlag)),
        this.store.select(fromRoot.getShowWelcomeModal)
      ]
    }),
    tap(([loc, eulaAccepted, activeMaintenance, showWelcomeModal]) => {
      if (showWelcomeModal && eulaAccepted && !activeMaintenance) {
        let config = new MatDialogConfig();
        config.data = this.us.getDiscoverButtons()
        config.autoFocus = false
        config.panelClass = 'dialog-responsive'
        this.dialog.open(WelcomeDialogComponent, config).afterClosed().subscribe(() => {
          this.store.dispatch(new user.DismissBannerAction(DiscoverConstants.welcomeModalBanner));
        });
      }
    }),
  )}, { dispatch : false });

  public navigateToCta: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.DISMISS_ALERT),
    map((action: any) => action.payload),
    filter((alertData) => alertData.isCta && alertData.alertCtaLink),
    tap((alertData) => {
      if (alertData.alertCtaLink.split('?').length >1) {
        let ctaLink = alertData.alertCtaLink.split('?');
        let params = { [ctaLink[1].split('=')[0]]: ctaLink[1].split('=')[1] }
        this.router.navigate([ctaLink[0]], { queryParams: params });
      } else {
        this.router.navigate([alertData.alertCtaLink]);

      }

    })
  )}, { dispatch : false });

  public applyDarkModeOnSettingsLoaded$: Observable<Action> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.USER_DATA_FOUND, settings.ActionTypes.LOAD_SETTINGS_SUCCESS, settings.ActionTypes.FAVORITE_LOCATIONS_UPDATED),
    concatLatestFrom(() => this.store),
    tap(([action, state]) => {
      const isDarkModeEnabled = !!state.user.userSettings?.darkModeEnabled;
      if (isDarkModeEnabled) {
        this.document.body.classList.add('spotlight-dark-mode');
        this.document.documentElement.classList.add('spotlight-dark-mode-preload');
      } else {
        this.document.body.classList.remove('spotlight-dark-mode');
        this.document.documentElement.classList.remove('spotlight-dark-mode-preload');
      }
      try {
        localStorage.setItem('spotlight_dark_mode', String(isDarkModeEnabled));
      } catch {
        // Ignore storage failures
      }
    }),
    switchMap(() => EMPTY)
  )}, { dispatch : false });

  constructor(
    private actions$: Actions,
    private snackBar: MatSnackBar,
    private store: Store<fromRoot.State>,
    private us: UserService,
    private router: Router,
    public dialog: MatDialog,
    private analytics: AnalyticsService,
    private ldService: LaunchDarklyService,
    private readonly http: HttpClient,
    private readonly refreshService: RefreshService,
    @Inject(DOCUMENT) private readonly document: Document
  ) { }

  private readonly campaignSource: CampaignSource = new DigitalMarketingCampaignSource(this.http);
}
